import { DefaultTFuncReturn } from 'i18next';
import { CartBundle, CartTicket } from 'types/CartItem';
import { Ticket, TicketPrice } from '../types/TicketsState';
import { formatMoney } from './formatMoney';
import {
  EventFeesData,
  TicketSalePageEvent,
} from 'app/pages/TicketSalePage/slice/types';
import { isBundle, isTicket, SelectedItem } from 'types/SelectedItem';
import {
  getTicketTypes,
  hasUnbundledAddons as bundleHasUnbundledAddons,
} from './bundles';

export const getTicketPriceByQuantity = (
  ticketRanges: TicketPrice[],
  quantity,
) => {
  if (ticketRanges.length === 1) return ticketRanges[0];

  const sortedRanges = [...ticketRanges].sort((a, b) => a.tickets - b.tickets);

  for (let i = 0; i < sortedRanges.length - 1; i++) {
    if (
      quantity >= sortedRanges[i].tickets &&
      quantity < sortedRanges[i + 1].tickets
    ) {
      return sortedRanges[i];
    }
  }
  return sortedRanges[sortedRanges.length - 1];
};

export const roundUpDecimals = (price: number): number =>
  Number.isInteger(price) ? price : parseFloat(price.toFixed(2));

export const buildPriceToShow = (
  value: number | null,
  currency: string,
  freeShow?: string | DefaultTFuncReturn,
) => {
  return value && value > 0
    ? `${formatMoney(roundUpDecimals(value), currency)}`
    : freeShow || formatMoney(0, currency);
};

export const applyDiscountToPriceWithoutRoundUp = (
  price: number,
  discount: number,
  discount_percent: boolean,
) => {
  let discountValue;

  if (discount_percent) {
    discountValue = (discount * price) / 100;
  } else {
    discountValue = discount;
  }

  // we need to round up to a certain number of decimals ( instead of just price - discountValue )
  // we had a case when we applied 100% voucher discount and
  // we calculated the original price 90.90909 minus the discountvalue 90.90909000000002
  // and this gave us this number -1.4210854715202004e-14 which is a veeery small one
  return Math.round((price - discountValue) * 1e12) / 1e12;
};

export const applyDiscountToPrice = (
  price: number,
  discount: number,
  discount_percent: boolean,
) => {
  let discountValue;

  if (discount_percent) {
    discountValue = (discount * price) / 100;
  } else {
    discountValue = discount;
  }

  return roundUpDecimals(price - discountValue);
};

export const buildVoucherPrice = (
  discount: number,
  currency: string | null,
  discount_percent: boolean,
) => (discount_percent ? `${discount}%` : formatMoney(discount, currency));

export const getTotalOfItem = (
  pricePerUnit: number,
  quantity: number | undefined,
): number => (quantity ? pricePerUnit * quantity : pricePerUnit);

export const hasUnbundledAddons = (ticket: Ticket) =>
  ticket.ticketaddons.some(({ is_bundled }) => !is_bundled);

// --- Tax and Fee Calculations ---
export const getTaxPrice = (price: number, percentage: number) =>
  price * (percentage / 100);

export const getFeePrice = (price: number, feesData: EventFeesData) => {
  if (price === 0) return 0;
  const feePriceByPercentage = price * (feesData.percent / 100);
  return Math.max(feePriceByPercentage, feesData.min);
};

export const getPriceWithoutTax = (price: number, percentage: number) =>
  price / (1 + percentage / 100);

// --- Addons Page ---

export const getDisplayedTickets = (
  selectedEvent: TicketSalePageEvent,
  selectedItems: SelectedItem[],
) => {
  const itemsWithUnbundledAddons = getItemsWithUnbundledAddons(
    selectedEvent,
    selectedItems,
  );

  return splitCartItemsToIndividualTickets(
    itemsWithUnbundledAddons,
    selectedEvent,
  );
};

const getItemsWithUnbundledAddons = (
  selectedEvent: TicketSalePageEvent,
  selectedItems: SelectedItem[],
) =>
  selectedItems.filter((item): item is CartTicket => {
    if (isTicket(item)) {
      const ticketData = selectedEvent.tickets.find(
        ({ id }) => id === item.ticketId,
      )!;

      return hasUnbundledAddons(ticketData);
    }
    if (isBundle(item)) {
      const bundleData = selectedEvent.ticketTypeBundles!.find(
        ({ id }) => id === item.id,
      )!;
      const ticketTypes = getTicketTypes(
        bundleData.items,
        selectedEvent.tickets,
      );
      return bundleHasUnbundledAddons(ticketTypes);
    }
    return false;
  });

export interface IndividualTicketType {
  ticketId: string;
  idOrderItem: string;
  name: string;
  date: string | undefined;
  bundleId: string | undefined;
}

export const splitCartItemsToIndividualTickets = (
  cartItems: (CartTicket | CartBundle)[],
  selectedEvent: TicketSalePageEvent,
) => {
  let tickets: IndividualTicketType[] = [];

  cartItems.forEach(item => {
    if (isTicket(item)) {
      for (let i = 1; i <= item.quantity; i++) {
        tickets.push({
          ticketId: item.ticketId,
          idOrderItem: item.idOrderItem,
          name: item.name,
          date: item.date,
          bundleId: undefined,
        });
      }
    }
    if (isBundle(item)) {
      const bundleData = selectedEvent.ticketTypeBundles!.find(
        ({ id }) => id === item.id,
      )!;
      bundleData.items.forEach(bundleItem => {
        const ticketName = selectedEvent.tickets.find(
          ({ id }) => id === bundleItem.idTicketType,
        )!.name;
        const orderItem = item.orderItems.find(
          ({ idTicketType }) => idTicketType === bundleItem.idTicketType,
        )!;

        for (let i = 1; i <= bundleItem.quantity * item.quantity; i++) {
          tickets.push({
            ticketId: bundleItem.idTicketType,
            idOrderItem: orderItem.id,
            name: ticketName,
            date: item.date,
            bundleId: item.id,
          });
        }
      });
    }
  });

  return tickets;
};

export const getTicketTypeTotalPriceWithoutDiscount = (
  event: TicketSalePageEvent,
  ticketType: Ticket,
  quantity: number,
) => {
  let total = 0;

  // price
  const ticketPriceData = getTicketPriceByQuantity(
    ticketType.ticketprice,
    quantity,
  );
  total += getTotalOfItem(ticketPriceData.price, quantity);

  // tax
  const tax = getTaxPrice(ticketPriceData.price, event.tax_rate.rate);
  total += getTotalOfItem(tax, quantity);

  // fee
  if (ticketPriceData.absorb_fees === false) {
    const fee = getFeePrice(ticketPriceData.price, event.fees);
    total += getTotalOfItem(fee, quantity);

    const feeTax = getTaxPrice(fee, event.fee_tax_rate.rate);
    total += getTotalOfItem(feeTax, quantity);
  }

  return total;
};
